import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import AiChat from './AiChat';
import AiConversationList from './AiConversationList';
import { AiConversation } from '../../services/api';

const AiAssistantPage: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [selectedConversation, setSelectedConversation] =
    useState<AiConversation | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [key, setKey] = useState(0); // Used to force remount AiChat

  const handleNewChat = () => {
    setSelectedConversation(null);
    setKey((prev) => prev + 1);
  };

  const handleSelectConversation = (conversation: AiConversation | null) => {
    setSelectedConversation(conversation);
    setKey((prev) => prev + 1);
  };

  const handleConversationChange = (conversationId: number) => {
    // Update selected conversation when a new one is created
    setSelectedConversation((prev) =>
      prev ? prev : ({ id: conversationId } as AiConversation),
    );
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}>
      {/* Ambient glow effect for dark mode */}
      {isDark && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-violet-600/10 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-purple-600/8 rounded-full blur-[120px]" />
        </div>
      )}

      <div className="h-[calc(100vh-4rem)] flex">
        {/* Sidebar Toggle for Mobile */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className={`lg:hidden fixed top-20 left-4 z-50 p-2.5 rounded-xl shadow-lg transition-all duration-200 ${
            isDark
              ? 'bg-slate-900 text-slate-300 hover:text-white hover:bg-slate-700'
              : 'bg-white text-slate-600 hover:text-slate-900 hover:bg-slate-50'
          }`}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {sidebarOpen ? (
              <path d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>

        {/* Sidebar */}
        <div
          className={`${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 fixed lg:relative z-40 w-80 h-full transition-transform duration-300 ease-in-out`}
        >
          <div className="h-full p-4">
            <AiConversationList
              selectedId={selectedConversation?.id}
              onSelect={handleSelectConversation}
              onNewChat={handleNewChat}
            />
          </div>
        </div>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className={`lg:hidden fixed inset-0 z-30 ${
              isDark ? 'bg-black/60' : 'bg-black/50'
            }`}
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Chat Area */}
        <div className="flex-1 p-4 lg:p-6 overflow-hidden">
          <AiChat
            key={key}
            conversationId={selectedConversation?.id}
            onConversationChange={handleConversationChange}
          />
        </div>
      </div>
    </div>
  );
};

export default AiAssistantPage;
